import { useState } from 'react';

import { useNewTranslations } from '@/common/translations';
import type { Option } from '@/common/types';
import { Filter, FilterChipGroup, Section } from '@/ui/components';

import { PlayersStats, PlayersStatsLegend } from '../../../components';
import { ApiWidgetType, PlayerStatsOptionValues, StatsTypes } from '../../../constants';
import type { PlayerStatsOption } from '../../../types';
import { useMapPlayersData, useStatsData } from '../../../hooks';

const TeamFilterOptionValues = {
  ALL: 'all',
  HOME_TEAM: 'homeTeam',
  AWAY_TEAM: 'awayTeam',
} as const;

type TeamFilterOption = (typeof TeamFilterOptionValues)[keyof typeof TeamFilterOptionValues];

const PlayersTab = () => {
  const translate = useNewTranslations();
  const { teams, widget } = useStatsData({
    widgetType: StatsTypes.PLAYER_STATS,
  });

  const mapPlayersData = useMapPlayersData({
    homeJerseySrc: teams.home.jersey,
    awayJerseySrc: teams.away.jersey,
    teams,
  });

  const [teamFilterValue, setTeamFilterValue] = useState<TeamFilterOption>(
    TeamFilterOptionValues.ALL,
  );
  const [playerStatsFilterValue, setPlayerStatsFilterValue] = useState<PlayerStatsOption>(
    PlayerStatsOptionValues.GOALS,
  );

  const teamFilterOptions: Option<TeamFilterOption>[] = [
    { label: translate('PreMatchStats.All'), value: TeamFilterOptionValues.ALL },
    { label: teams.home.name, value: TeamFilterOptionValues.HOME_TEAM },
    { label: teams.away.name, value: TeamFilterOptionValues.AWAY_TEAM },
  ];

  const playerStatsFilterOptions: Option<PlayerStatsOption>[] = [
    { label: translate('PreMatchStats.Goals'), value: PlayerStatsOptionValues.GOALS },
    { label: translate('PreMatchStats.Assists'), value: PlayerStatsOptionValues.ASSISTS },
    { label: translate('PreMatchStats.Shots'), value: PlayerStatsOptionValues.SHOTS },
    { label: translate('PreMatchStats.Cards'), value: PlayerStatsOptionValues.CARDS },
    { label: translate('PreMatchStats.Injured'), value: PlayerStatsOptionValues.INJURED },
  ];

  if (!widget || widget.type !== ApiWidgetType.PLAYER_STATS || !widget?.data) {
    return null;
  }

  const mappedPlayersData = mapPlayersData(widget.data);

  const getPlayersDataToDisplay = () => {
    if (!mappedPlayersData) {
      return [];
    }

    switch (teamFilterValue) {
      case TeamFilterOptionValues.HOME_TEAM:
        return mappedPlayersData[playerStatsFilterValue].filter(({ isHome }) => isHome);
      case TeamFilterOptionValues.AWAY_TEAM:
        return mappedPlayersData[playerStatsFilterValue].filter(({ isHome }) => !isHome);
      case TeamFilterOptionValues.ALL:
      default:
        return mappedPlayersData[playerStatsFilterValue];
    }
  };

  return (
    <Section
      name={translate('PreMatchStats.PlayerStats')}
      contentClassName="p-3 flex flex-col gap-3"
    >
      <Filter
        variant="outlined"
        options={teamFilterOptions}
        value={teamFilterValue}
        onValueChange={setTeamFilterValue}
      />
      <FilterChipGroup
        className="mx-[-12px] px-[12px]"
        isTogglable={false}
        options={playerStatsFilterOptions}
        value={playerStatsFilterValue}
        onValueChange={setPlayerStatsFilterValue}
      />
      <Section>
        <PlayersStats
          activeFilter={playerStatsFilterValue}
          playersData={getPlayersDataToDisplay()}
        />
      </Section>
      <PlayersStatsLegend activeFilter={playerStatsFilterValue} />
    </Section>
  );
};

export default PlayersTab;
